import React, { useEffect, useState, useMemo } from "react";
import axios from "axios";
import { useUser } from "../../Context/UserContext";
import { DataGrid, GridToolbar, useGridApiRef } from "@mui/x-data-grid";
import {
  Box,
  Button,
  TextField,
  InputAdornment,
  Chip,
  Tooltip,
  Typography,
  Autocomplete
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useParams, useNavigate } from "react-router-dom";

const BlocksaplingReport = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { token } = useUser();
  const apiRef = useGridApiRef();

  const [reportData, setReportData] = useState([]);
  const [blockList, setBlockList] = useState([]);
  const [saplingOptions, setSaplingOptions] = useState([]);

  // Filter States
  const [selectedBlock, setSelectedBlock] = useState(null);
  const [selectedSapling, setSelectedSapling] = useState(null);
  const [searchText, setSearchText] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!token || !id) return;

    setLoading(true);
    axios
      .get(`/api/center/block-sapling-summary/?dist_id=${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((res) => {
        if (res.data && res.data.saplings) {
          setReportData(res.data.saplings);

          const uniqueSaplings = Array.from(
            new Set(res.data.saplings.map((s) => s.sapling_name))
          )
            .filter(Boolean)
            .sort()
            .map((name) => ({ name }));
          setSaplingOptions(uniqueSaplings);

          if (res.data.all_blocks) {
            setBlockList(
              res.data.all_blocks.map((b) => ({
                id: String(b.id),
                name: b.Block_Name,
              }))
            );
          }
        }
      })
      .catch((err) => console.error("Error fetching data:", err))
      .finally(() => setLoading(false));
  }, [token, id]);

  const filteredData = useMemo(() => {
    return reportData.filter((row) => {
      const matchesSearch =
        !searchText ||
        (row.sapling_name &&
          row.sapling_name.toLowerCase().includes(searchText.toLowerCase()));

      const matchesBlock =
        !selectedBlock ||
        (row.block_names &&
          row.block_names.some((b) => String(b.id) === String(selectedBlock.id)));

      const matchesSapling =
        !selectedSapling || row.sapling_name === selectedSapling.name;

      return matchesSearch && matchesBlock && matchesSapling;
    });
  }, [reportData, selectedBlock, selectedSapling, searchText]);

  const columns = useMemo(
    () => [
      {
        field: "sno",
        headerName: "S.No",
        width: 70,
        align: "center",
        renderCell: (params) => params.api.getAllRowIds().indexOf(params.id) + 1,
      },
      {
        field: "sapling_name",
        headerName: "Sapling Name",
        flex: 1,
        minWidth: 180,
        renderCell: (params) => (
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {params.value}
          </Typography>
        ),
      },
      {
        field: "block_names",
        headerName: "Blocks",
        flex: 1.5,
        minWidth: 250,
        renderCell: (params) => {
          const blocks = params.row.block_names || [];
          if (blocks.length === 0) return <Typography sx={{ color: "#aaa" }}>-</Typography>;
          return (
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
              {blocks.slice(0, 2).map((block) => (
                <Chip
                  key={block.id}
                  label={block.name}
                  size="small"
                  clickable
                  onClick={() => navigate(`/block/${block.id}/CenterSaplingReport`)}
                  sx={{ bgcolor: "#e3f2fd", color: "#1565c0", border: "1px solid #bbdefb" }}
                />
              ))}
              {blocks.length > 2 && (
                <Tooltip title={blocks.slice(2).map((b) => b.name).join(", ")}>
                  <Chip label={`+${blocks.length - 2}`} size="small" />
                </Tooltip>
              )}
            </Box>
          );
        },
      },
      // RESTORED PRODUCTION CENTERS COLUMN
      {
        field: "center_names",
        headerName: "Production Centers",
        minWidth: 220,
        flex: 1.5,
        renderCell: (params) => {
          let displayValue = params.row.productioncenter_id;
          if (!displayValue) return <Typography sx={{ color: "#aaa" }}>-</Typography>;
          const items = String(displayValue).split(",").map((i) => i.trim()).filter(Boolean);
          return (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 0.3, py: 0.5 }}>
              <Typography variant="caption" sx={{ color: "#555", pl: 1, borderLeft: "2px solid #ff9800" }}>
                {items.length} Centers
              </Typography>
            </Box>
          );
        },
      },
      {
        field: "total_stock",
        headerName: "Total Stock",
        type: "number",
        width: 120,
        renderCell: (p) => (
          <Typography sx={{ fontWeight: 700, color: p.value > 0 ? "#2e7d32" : "#999" }}>
            {p.value?.toLocaleString("en-IN") || 0}
          </Typography>
        ),
      },
      {
        field: "total_sold",
        headerName: "Total Sold",
        type: "number",
        width: 120,
        renderCell: (p) => (
          <Typography sx={{ fontWeight: 700, color: p.value > 0 ? "#e65100" : "#999" }}>
            {p.value?.toLocaleString("en-IN") || 0}
          </Typography>
        ),
      },
      {
        field: "total_sale_price",
        headerName: "Total Value (₹)",
        width: 160,
        renderCell: (p) => (
          <Typography sx={{ fontWeight: 700, color: "#1565c0" }}>
            ₹{Number(p.value || 0).toLocaleString("en-IN")}
          </Typography>
        ),
      },
    ],
    [navigate]
  );

  const handleExport = () => {
    const rows = Array.from(apiRef.current.getRowModels().values());
    const headers = ["S.No", "Sapling Name", "Blocks", "Centers", "Total Stock", "Total Sold", "Total Value (₹)"];
    const csvRows = rows.map((row, index) => [
      index + 1,
      `"${row.sapling_name || ""}"`,
      `"${(row.block_names || []).map((b) => b.name).join(", ")}"`,
      `"${row.productioncenter_id || ""}"`,
      row.total_stock || 0,
      row.total_sold || 0,
      row.total_sale_price || 0,
    ].join(","));
    const blob = new Blob([[headers.join(","), ...csvRows].join("\n")], { type: "text/csv" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = "Block_Sapling_Summary.csv";
    link.click();
  };

  return (
    <Box sx={{ p: 2, height: "calc(100vh - 60px)", display: "flex", flexDirection: "column", bgcolor: "#f4f6f8" }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2, p: 2, bgcolor: "white", borderRadius: 2, boxShadow: "0 2px 4px rgba(0,0,0,0.05)" }}>
        <Box sx={{ display: "flex", gap: 1.5, alignItems: "center", flexWrap: "wrap" }}>
          <Button variant="outlined" startIcon={<ArrowBackIcon />} onClick={() => navigate(-1)} size="small" sx={{ textTransform: "none" }}>
            Back
          </Button>

          <TextField
            size="small"
            placeholder="Search sapling..."
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
            sx={{ width: 180 }}
          />

          <Autocomplete
            size="small"
            options={blockList}
            getOptionLabel={(option) => option.name || ""}
            value={selectedBlock}
            onChange={(e, val) => setSelectedBlock(val)}
            renderInput={(params) => <TextField {...params} label="Select Block" />}
            sx={{ width: 180 }}
          />

          <Autocomplete
            size="small"
            options={saplingOptions}
            getOptionLabel={(option) => option.name || ""}
            value={selectedSapling}
            onChange={(e, val) => setSelectedSapling(val)}
            renderInput={(params) => <TextField {...params} label="Select Sapling" />}
            sx={{ width: 180 }}
          />

          <Button 
            size="small" 
            onClick={() => { setSelectedBlock(null); setSelectedSapling(null); setSearchText(""); }}
            sx={{ color: "#666", textTransform: "none" }}
          >
            Reset
          </Button>
        </Box>

        <Button variant="contained" onClick={handleExport} sx={{ bgcolor: "#1565c0", textTransform: "none" }}>
          Download CSV
        </Button>
      </Box>

      <Box sx={{ flexGrow: 1, bgcolor: "white", borderRadius: 2, overflow: "hidden", boxShadow: "0 4px 12px rgba(0,0,0,0.08)" }}>
        <DataGrid
          apiRef={apiRef}
          rows={filteredData}
          columns={columns}
          loading={loading}
          getRowId={(row) => row.species_id || Math.random()}
          density="comfortable"
          slots={{ toolbar: GridToolbar }}
          disableRowSelectionOnClick
        />
      </Box>
    </Box>
  );
};

export default BlocksaplingReport;